import type { Store } from 'effector'

import { LiveEventsTypes } from '@setplex/tria-api'
import { combine, createEvent, createStore, split } from 'effector'
import { options } from '~/core/flags'
import { model as i18n } from '~/core/i18n'
import {
  formatLiveEventWithPrice,
  formatPastEventWithPrice,
  model as formattedEventsEntityModel,
} from '~/entities/live-events/events'
import { ssePurchasesModel } from '~/features/payment/sse-purchases'
import { FORMAT_LIVE_EVENTS_TYPES } from './lib'

type EventsTabPayload = {
  id: LiveEventsTypes
  page?: number
  count?: number
}

export const changeActiveTab = createEvent<{ id: LiveEventsTypes }>()
export const $activeTab = createStore<LiveEventsTypes | null>(null).on(
  changeActiveTab,
  (_, { id }) => id
)
export const $formatActiveTab = $activeTab.map((tab) =>
  tab == null ? null : FORMAT_LIVE_EVENTS_TYPES[tab]
)

export const $preferredTab = createStore<LiveEventsTypes>(
  LiveEventsTypes.LIVE
).on(changeActiveTab, (_, { id }) => id)

export const loadMoreEvents = createEvent<EventsTabPayload>()

// TODO: perhaps place $pastEventsEnabled in more suitable layer where we can reuse it in all upper layers
export const $pastEventsEnabled = options.$liveEventRecordsEnabled

export const $isAnyEvents = combine(
  [
    formattedEventsEntityModel.$liveEvents,
    formattedEventsEntityModel.$upcomingEvents,
    formattedEventsEntityModel.$pastEvents,
  ],
  (events) => events?.some((item) => item?.length)
)

type EventsTabNames = {
  live: string
  upcoming: string
  past: string
}

export const $eventsTabNames: Store<EventsTabNames> = combine(
  i18n.$t,
  ({ t }) => ({
    live: t('live_now'),
    upcoming: t('upcoming'),
    past: t('events_past'),
  })
)

export const $allEvents = combine(
  {
    live: formattedEventsEntityModel.$liveEvents,
    upcoming: formattedEventsEntityModel.$upcomingEvents,
    past: formattedEventsEntityModel.$pastEvents,
    pastEventsEnabled: options.$liveEventRecordsEnabled,
    name: $eventsTabNames,
    activeTab: $activeTab,
    adjust: ssePurchasesModel.$liveEvent,
  },
  ({ live, upcoming, past, name, activeTab, pastEventsEnabled, adjust }) => [
    {
      contentType: LiveEventsTypes.LIVE,
      title: name.live,
      list: live.map((event) => formatLiveEventWithPrice(adjust(event))),
      selected: LiveEventsTypes.LIVE === activeTab,
      hasContent: Boolean(live?.length),
    },
    {
      contentType: LiveEventsTypes.UPCOMING,
      title: name.upcoming,
      list: upcoming.map((event) => formatLiveEventWithPrice(adjust(event))),
      selected: LiveEventsTypes.UPCOMING === activeTab,
      hasContent: Boolean(upcoming?.length),
    },
    {
      contentType: LiveEventsTypes.PAST,
      title: name.past,
      list: past.map((event) => formatPastEventWithPrice(adjust(event))),
      selected: LiveEventsTypes.PAST === activeTab,
      hasContent: Boolean(past?.length) && pastEventsEnabled,
    },
  ]
)

split({
  source: loadMoreEvents,
  match: $activeTab,
  cases: {
    [LiveEventsTypes.LIVE]: formattedEventsEntityModel.loadMoreLiveEvents,
    [LiveEventsTypes.UPCOMING]:
      formattedEventsEntityModel.loadMoreUpcomingEvents,
    [LiveEventsTypes.PAST]: formattedEventsEntityModel.loadMorePastEvents,
  },
})
